import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import "./RestaurantSelectionModal.scss";
import { Restaurant } from "../../../interfaces/Catalogue";
import { useConfig } from "../../../context/ConfigContext";
import appConfig from "../../../appConfig";
import { buildIconWithBg } from "../../../utils/iconUtils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/rootState";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import GpsOffIcon from "@mui/icons-material/GpsOff";
import DirectionsIcon from "@mui/icons-material/Directions";
import { updateAppState } from "../../../store/app/appActions";

interface RestaurantSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (restaurant: Restaurant) => void;
}

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconX = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconPin = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconClock = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconStar = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const IconCheck = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconNavigation = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="3 11 22 2 13 21 11 13 3 11" />
  </svg>
);

const IconUtensils = ({ size = 32 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
    <path d="M7 2v20" /><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
  </svg>
);

// ── Haversine distance (km) ───────────────────────────────────────────────────
const haversine = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ── Open Google Maps directions ───────────────────────────────────────────────
const openDirections = (
  restaurant: Restaurant,
  userLocation: { lat: number; lng: number } | null
) => {
  const dest = restaurant.latitude !== 0
    ? `${restaurant.latitude},${restaurant.longitude}`
    : encodeURIComponent(restaurant.address ?? restaurant.nom);
  const origin = userLocation ? `${userLocation.lat},${userLocation.lng}` : "";
  const url = origin
    ? `https://www.google.com/maps/dir/${origin}/${dest}`
    : `https://www.google.com/maps/search/?api=1&query=${dest}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

// ── Blue user-dot icon as inline SVG data URL ─────────────────────────────────
const userDotIcon = (): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
    <circle cx="14" cy="14" r="13" fill="#4285F4" fill-opacity="0.18"/>
    <circle cx="14" cy="14" r="7"  fill="#4285F4"/>
    <circle cx="14" cy="14" r="3"  fill="#fff"/>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

// ─────────────────────────────────────────────────────────────────────────────
const RestaurantSelectionModal: React.FC<RestaurantSelectionModalProps> = ({
  visible,
  onClose,
  onSelect,
}) => {
  const { config } = useConfig();
  const dispatch = useDispatch();
  const primaryColor = config?.primaryColor || "#6366F1";
  const logoUrl = config?.appLogoImage || "";


  const [search, setSearch] = useState("");
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationStatus, setLocationStatus] = useState<"idle" | "loading" | "granted" | "denied">("idle");
  const [toast, setToast] = useState<string | null>(null);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const markerMapRef = useRef<Map<number, google.maps.Marker>>(new Map());
  const [mapsReady, setMapsReady] = useState(!!window.google?.maps);

  const selectedRestaurant = useSelector((state: RootState) => state.shop.selectedRestaurant);
  const favoriteRestaurantId = useSelector((state: RootState) => state.app.favoriteRestaurantId);
  const restaurants = useSelector((state: RootState) => state.shop.shops);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };


  const handleSelect = (restaurant: Restaurant) => {

    dispatch(updateAppState({ favoriteRestaurantId: restaurant.id }));
    onSelect(restaurant);

  }


  // ── Lock body scroll ──────────────────────────────────────────────────────
  useEffect(() => {
    if (visible) {
      const scrollY = window.scrollY;
      document.body.style.cssText = `overflow:hidden;position:fixed;top:-${scrollY}px;width:100%`;
    } else {
      const top = document.body.style.top;
      document.body.style.cssText = "";
      window.scrollTo(0, parseInt(top || "0") * -1);
    }
    return () => { document.body.style.cssText = ""; };
  }, [visible]);

  // ── Auto-request location on open ────────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    setLocationStatus("loading");
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationStatus("granted");
      },
      () => setLocationStatus("denied")
    );
  }, [visible]);

  // ── Place / update blue user-dot on the map ───────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapsReady) return;
    if (!userLocation) { userMarkerRef.current?.setMap(null); userMarkerRef.current = null; return; }

    const pos = { lat: userLocation.lat, lng: userLocation.lng };
    if (userMarkerRef.current) {
      userMarkerRef.current.setPosition(pos);
    } else {
      userMarkerRef.current = new window.google.maps.Marker({
        position: pos, map,
        title: "Ma position",
        icon: {
          url: userDotIcon(),
          scaledSize: new window.google.maps.Size(28, 28),
          anchor: new window.google.maps.Point(14, 14),
        },
        zIndex: 20,
        clickable: false,
      });
    }
  }, [userLocation, mapsReady]);

  // ── Recenter map on user ──────────────────────────────────────────────────
  const recenterMap = useCallback(() => {
    if (!mapInstanceRef.current || !userLocation) return;
    mapInstanceRef.current.panTo({ lat: userLocation.lat, lng: userLocation.lng });
    mapInstanceRef.current.setZoom(14);
  }, [userLocation]);

  // ── Location button handler ───────────────────────────────────────────────
  const requestLocation = () => {
    if (locationStatus === "loading") return;
    // If already granted, just recenter the map
    if (locationStatus === "granted") { recenterMap(); return; }

    setLocationStatus("loading");
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        setLocationStatus("granted");
        mapInstanceRef.current?.panTo(loc);
        mapInstanceRef.current?.setZoom(14);
      },
      () => {
        setLocationStatus("denied");
        showToast("Accès à la position refusé. Activez-la dans les paramètres du navigateur.");
      }
    );
  };

  // ── Load Google Maps script ───────────────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    if (window.google?.maps) { setMapsReady(true); return; }
    if (!appConfig.GOOGLE_MAPS_API_KEY) return;
    if (document.querySelector('script[src*="maps.googleapis.com"]')) return;

    const cb = "__initSelectionMap__";
    (window as any)[cb] = () => setMapsReady(true);
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${appConfig.GOOGLE_MAPS_API_KEY}&callback=${cb}`;
    s.async = true; s.defer = true;
    document.head.appendChild(s);
    return () => { delete (window as any)[cb]; };
  }, [visible]);

  // ── Geocode helper ────────────────────────────────────────────────────────
  const resolveLatLng = (r: Restaurant): Promise<google.maps.LatLngLiteral | null> => {
    if (r.latitude !== 0 && r.longitude !== 0)
      return Promise.resolve({ lat: r.latitude, lng: r.longitude });
    if (!r.address) return Promise.resolve(null);
    return new Promise((resolve) => {
      new window.google.maps.Geocoder().geocode({ address: r.address! }, (results, status) => {
        resolve(
          status === "OK" && results?.[0]
            ? { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() }
            : null
        );
      });
    });
  };

  // ── Build map + markers ───────────────────────────────────────────────────
  useEffect(() => {
    if (!mapsReady || !visible || !mapRef.current || !restaurants.length) return;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: userLocation ?? { lat: 48.8566, lng: 2.3522 },
      disableDefaultUI: true,
      gestureHandling: "cooperative",
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
    });
    mapInstanceRef.current = map;
    markerMapRef.current.clear();

    const iw = new window.google.maps.InfoWindow();
    infoWindowRef.current = iw;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const fallback: google.maps.Symbol = {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 12,
      fillColor: primaryColor,
      fillOpacity: 1,
      strokeColor: "#fff",
      strokeWeight: 2,
    };

    const init = async () => {
      const iconDataUrl = logoUrl ? await buildIconWithBg(logoUrl, config?.layoutColor) : null;
      const markerIcon: google.maps.Icon | undefined = iconDataUrl
        ? { url: iconDataUrl, scaledSize: new window.google.maps.Size(36, 36), anchor: new window.google.maps.Point(18, 36) }
        : undefined;

      const positions = await Promise.all(restaurants.map(resolveLatLng));
      const bounds = new window.google.maps.LatLngBounds();
      let any = false;

      restaurants.forEach((r, i) => {
        const pos = positions[i];
        if (!pos) return;
        any = true;
        bounds.extend(pos);

        const isSelected = selectedRestaurant?.id === r.id;
        const marker = new window.google.maps.Marker({
          position: pos, map, title: r.nom,
          icon: markerIcon ?? fallback,
          animation: window.google.maps.Animation.DROP,
          zIndex: isSelected ? 10 : 1,
        });
        markersRef.current.push(marker);
        markerMapRef.current.set(r.id, marker);

        marker.addListener("click", () => {
          // Scroll the list item into view
          itemRefs.current[r.id]?.scrollIntoView({ behavior: "smooth", block: "nearest" });

          iw.setContent(`
            <div style="font-family:system-ui,sans-serif;padding:2px;min-width:175px;">
              <p style="font-size:13px;font-weight:700;color:#111827;margin:0 0 3px;">${r.nom}</p>
              ${r.address ? `<p style="font-size:11px;color:#6b7280;margin:0 0 6px;">${r.address}</p>` : ""}
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
                <span style="font-size:10px;font-weight:600;padding:2px 6px;border-radius:5px;
                  background:${r.isOpen ? "#d1fae5" : "#fee2e2"};
                  color:${r.isOpen ? "#059669" : "#dc2626"};">
                  ${r.isOpen ? "Ouvert" : "Fermé"}
                </span>
                ${r.tempsPrep > 0 ? `<span style="font-size:10px;color:#6b7280;">${r.tempsPrep} min</span>` : ""}
              </div>
              <div style="display:flex;gap:6px;">
                ${r.isOpen && selectedRestaurant?.id !== r.id ? `
                  <button id="msm-sel-${r.id}" style="flex:1;padding:6px;border-radius:7px;border:none;
                    background:${primaryColor};color:#fff;font-size:12px;font-weight:600;cursor:pointer;">
                    Choisir
                  </button>` : selectedRestaurant?.id === r.id ? `
                  <p style="text-align:center;font-size:11px;font-weight:600;color:${primaryColor};margin:0;flex:1;">✓ Sélectionné</p>
                ` : ""}
                <button id="msm-dir-${r.id}" style="padding:6px 10px;border-radius:7px;
                  border:1.5px solid #e5e7eb;background:#fff;color:#374151;
                  font-size:12px;font-weight:600;cursor:pointer;">
                  🧭 Itinéraire
                </button>
              </div>
            </div>
          `);
          iw.open(map, marker);

          window.google.maps.event.addListenerOnce(iw, "domready", () => {
            document.getElementById(`msm-sel-${r.id}`)?.addEventListener("click", () => {
              handleSelect(r); iw.close();
            });
            document.getElementById(`msm-dir-${r.id}`)?.addEventListener("click", () => {
              openDirections(r, userLocation);
            });
          });
        });
      });

      if (any) {
        if (userLocation) {
          map.panTo({ lat: userLocation.lat, lng: userLocation.lng });
          map.setZoom(13);
        } else {
          map.fitBounds(bounds);
          window.google.maps.event.addListenerOnce(map, "idle", () => {
            if ((map.getZoom() ?? 0) > 15) map.setZoom(15);
          });
        }
      }
    };

    init();
  }, [mapsReady, visible, restaurants, selectedRestaurant, primaryColor, logoUrl]);

  // ── Pan map when userLocation arrives after map is built ──────────────────
  useEffect(() => {
    if (!mapInstanceRef.current || !userLocation) return;
    mapInstanceRef.current.panTo({ lat: userLocation.lat, lng: userLocation.lng });
    mapInstanceRef.current.setZoom(13);
  }, [userLocation]);

  // ── List item click → pan map + trigger marker click ─────────────────────
  const handleItemClick = (item: Restaurant & { _dist: number | null }) => {
    handleSelect(item);
    const marker = markerMapRef.current.get(item.id);
    if (marker && mapInstanceRef.current) {
      mapInstanceRef.current.panTo(marker.getPosition()!);
      mapInstanceRef.current.setZoom(15);
      window.google.maps.event.trigger(marker, "click");
    }
  };

  // ── Processed + sorted list ───────────────────────────────────────────────
  const processed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return restaurants
      .map((r) => {
        const serverDist = (r.distance ?? 0) as number;
        let dist = serverDist > 0 ? serverDist / 1000 : null;
        if (dist === null && userLocation && r.latitude !== 0) {
          dist = haversine(userLocation.lat, userLocation.lng, r.latitude, r.longitude);
        }
        return { ...r, _dist: dist };
      })
      .filter((r) => !q || r.nom.toLowerCase().includes(q) || (r.address ?? "").toLowerCase().includes(q))
      .sort((a, b) => {
        if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
        if (a._dist !== null && b._dist !== null) return a._dist - b._dist;
        if (a._dist !== null) return -1;
        if (b._dist !== null) return 1;
        return 0;
      });
  }, [restaurants, search, userLocation]);

  if (!visible) return null;

  return (
    <div className="rsm-overlay" onClick={onClose}>
      <div className="rsm-sheet" onClick={(e) => e.stopPropagation()}>

        <div className="rsm-handle" />

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="rsm-header">
          <div className="rsm-header-left">
            <p className="rsm-title">Choisissez votre restaurant</p>
            <p className="rsm-sub">
              {processed.length} disponible{processed.length !== 1 ? "s" : ""}
              {userLocation ? " · trié par distance" : ""}
            </p>
          </div>
          {favoriteRestaurantId && (
            <button className="rsm-close" onClick={onClose} aria-label="Fermer">
              <IconX size={14} />
            </button>
          )}
        </div>

        {/* ── Search ─────────────────────────────────────────── */}
        <div className="rsm-search-wrap">
          <span className="rsm-search-icon"><IconSearch /></span>
          <input
            className="rsm-search"
            type="text"
            placeholder="Rechercher un restaurant ou une adresse…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
          {search && (
            <button className="rsm-search-clear" onClick={() => setSearch("")} aria-label="Effacer">
              <IconX size={12} />
            </button>
          )}

          {/* Location icon */}
          <button
            className={`rsm-location-btn rsm-location-btn--${locationStatus}`}
            onClick={requestLocation}
            aria-label="Ma position"
            title={
              locationStatus === "granted" ? "Recentrer sur ma position"
                : locationStatus === "denied" ? "Accès refusé — cliquez pour réessayer"
                  : locationStatus === "loading" ? "Localisation en cours…"
                    : "Utiliser ma position"
            }
          >
            {locationStatus === "granted" ? (
              <GpsFixedIcon style={{ fontSize: 18 }} />
            ) : locationStatus === "denied" ? (
              <GpsOffIcon style={{ fontSize: 18 }} />
            ) : (
              <MyLocationIcon
                style={{ fontSize: 18 }}
                className={locationStatus === "loading" ? "rsm-spin" : ""}
              />
            )}
          </button>
        </div>

        {/* ── Toast ──────────────────────────────────────────── */}
        {toast && (
          <div className="rsm-toast">
            <GpsOffIcon style={{ fontSize: 15 }} />
            {toast}
          </div>
        )}

        {/* ── Mini map ───────────────────────────────────────── */}
        {appConfig.GOOGLE_MAPS_API_KEY && (
          <div className="rsm-map-wrap">
            {!mapsReady && (
              <div className="rsm-map-loading">
                <span className="rsm-spinner" />
              </div>
            )}
            <div ref={mapRef} className="rsm-map-canvas" />

            {/* Recenter FAB */}
            {locationStatus === "granted" && mapsReady && (
              <button
                className="rsm-map-recenter"
                onClick={recenterMap}
                aria-label="Recentrer sur ma position"
                title="Ma position"
              >
                <MyLocationIcon style={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        )}

        {/* ── List ───────────────────────────────────────────── */}
        <div className="rsm-list">
          {processed.length === 0 ? (
            <div className="rsm-empty">
              <span className="rsm-empty-icon"><IconUtensils size={36} /></span>
              <p>Aucun restaurant trouvé</p>
            </div>
          ) : (
            processed.map((item, index) => {
              const isSelected = selectedRestaurant?.id === item.id;
              const isNearest = index === 0 && item._dist !== null;

              return (
                <div key={item.id}>
                  <div
                    ref={(el) => (itemRefs.current[item.id] = el)}
                    className={[
                      "rsm-item",
                      isSelected ? "rsm-item--selected" : "",
                      !item.isOpen ? "rsm-item--closed" : "",
                    ].filter(Boolean).join(" ")}
                    style={isSelected ? { backgroundColor: `${primaryColor}0a` } : {}}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="rsm-item-body">

                      {(isNearest || isSelected) && (
                        <div className="rsm-item-tags">
                          {isNearest && (
                            <span className="rsm-tag rsm-tag--nearest">
                              <IconNavigation size={9} />
                              Le plus proche
                            </span>
                          )}
                          {isSelected && (
                            <span className="rsm-tag rsm-tag--selected" style={{ background: primaryColor }}>
                              <IconCheck size={9} />
                              Sélectionné
                            </span>
                          )}
                        </div>
                      )}

                      <p className="rsm-item-name" style={isSelected ? { color: primaryColor } : {}}>
                        {item.nom}
                      </p>

                      {item.address && (
                        <p className="rsm-item-addr">
                          <IconPin size={11} />
                          {item.address}
                        </p>
                      )}

                      <div className="rsm-item-meta">
                        <span className={`rsm-badge ${item.isOpen ? "rsm-badge--open" : "rsm-badge--closed"}`}>
                          <span className="rsm-badge-dot" />
                          {item.isOpen ? "Ouvert" : "Fermé"}
                        </span>

                        {item._dist !== null && (
                          <span className="rsm-chip">
                            <IconPin size={10} />
                            {item._dist < 1
                              ? `${Math.round(item._dist * 1000)} m`
                              : `${item._dist.toFixed(1)} km`}
                          </span>
                        )}

                        {item.tempsPrep > 0 && (
                          <span className="rsm-chip">
                            <IconClock size={10} />
                            {item.tempsPrep} min
                          </span>
                        )}

                        {item.note > 0 && (
                          <span className="rsm-chip rsm-chip--star">
                            <IconStar size={10} />
                            {item.note.toFixed(1)}
                          </span>
                        )}

                        {item.shifts?.[0] && (
                          <span className="rsm-chip">
                            <IconClock size={10} />
                            {item.shifts[0].open.slice(0, 5)}–{item.shifts[0].close.slice(0, 5)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right side actions */}
                    <div className="rsm-item-actions">
                      <button
                        className="rsm-dir-btn"
                        onClick={(e) => { e.stopPropagation(); openDirections(item, userLocation); }}
                        aria-label="Itinéraire"
                        title="Ouvrir l'itinéraire dans Google Maps"
                      >
                        <DirectionsIcon style={{ fontSize: 20 }} />
                      </button>

                      {isSelected && (
                        <div className="rsm-check" style={{ background: primaryColor }}>
                          <IconCheck size={13} />
                        </div>
                      )}
                    </div>
                  </div>

                  {index < processed.length - 1 && <div className="rsm-divider" />}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantSelectionModal;